import React, { useCallback, useState } from 'react';
import {
  Alert, Badge, Box, Button, Card, Group, Modal, Stack, Text, Title, Tooltip,
} from '@mantine/core';
import { IconAlertCircle, IconCheck, IconCoin, IconSparkles } from '@tabler/icons-react';

import {
  getProject, getDesignModel, submitGeneration, answerOpenQuestion, applyDesignEdits, uploadSource,
  approveDesign,
} from '../api_scripts/solnStudioClient';
import { extractText } from '../utils/extractText';
import { extractWorksheetAnswers } from '../utils/customerWorksheet';
import { decideLexStrategy } from '../utils/lexStrategy';
import { diffDddWorkbook } from '../utils/dddRoundTrip';
import { regenerateDeliverables } from '../utils/designDeliverables';
import { timeAgo, formatUsd, hasDesignContent } from '../utils/format';
import useJobPoll from '../hooks/useJobPoll';
import ActionHelp from '../components/ActionHelp';
import ChangeSummary from '../components/ChangeSummary';
import JobStatusAlert from '../components/JobStatusAlert';
import NluGradeCard from '../components/NluGradeCard';
import DocumentsPanel from './DocumentsPanel';
import OpenQuestionsSection from './OpenQuestionsPanel';

export default function DesignPanel({ project, designModel, getApiToken, onChanged, locked }) {
  const [submitting, setSubmitting] = useState(false);
  const [estimateModal, setEstimateModal] = useState(null); // { estimate, jobId, kind, mode }
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null); // transient success line (e.g. "Applied N answers")
  const [needRefine, setNeedRefine] = useState(false); // an upload-revised left freeform changes to fold in
  const [dddImport, setDddImport] = useState(null); // { diff } — DDD round-trip preview before applying
  const [importBusy, setImportBusy] = useState(false);
  const [docBusy, setDocBusy] = useState(null); // deliverable id currently building/uploading
  const [approving, setApproving] = useState(false);

  // §P2.5 design sign-off: approval is per design-model VERSION — a later refine bumps the version,
  // so the approval goes stale automatically and Build's soft gate asks again.
  const approvedCurrent = !!project.designApprovedVersion && project.designApprovedVersion === project.designModelVersion;

  // What Lex/NLU deliverable this design warrants (DTMF-only → none; large NLU → standalone worksheet).
  const lexStrategy = designModel ? decideLexStrategy(designModel) : null;
  const hasModel = hasDesignContent(designModel);

  const counts = designModel
    ? {
        intents: designModel.intents?.length || 0,
        prompts: designModel.prompts?.length || 0,
        flows: designModel.flows?.length || 0,
        openQuestions: designModel.openQuestions?.length || 0,
      }
    : null;

  // (Re)build + upload the applicable deliverables from the freshest design model. Non-fatal: a
  // failure (e.g. the AWS export template fetch) surfaces a warning but never blocks the design.
  const refreshDeliverables = useCallback(async (only) => {
    setDocBusy(only && only.length === 1 ? only[0] : '*');
    try {
      const proj = await getProject(project.projectId, getApiToken);
      const dm = await getDesignModel(project.projectId, getApiToken);
      await regenerateDeliverables({
        projectId: project.projectId,
        model: dm.designModel,
        designModelVersion: proj.designModelVersion || 0,
        expectedVersion: proj.version,
        getApiToken,
        only,
      });
      onChanged();
    } catch (err) {
      setError(`Couldn't refresh documents: ${err.message}`);
    } finally {
      setDocBusy(null);
    }
  }, [project.projectId, getApiToken, onChanged]);

  // Shared job loop (P0.4): resumes an in-flight design refine on mount (tab switch / reload) and
  // keeps the terminal result rendered until dismissed. onChanged is a BACKGROUND refresh (P0.3) —
  // the result panel below survives it.
  const { job, stalled, running, start, dismiss } = useJobPoll({
    project,
    getApiToken,
    match: (j) => j.kind === 'design' && (j.stage || 'design') === 'design',
    onTerminal: async (j) => {
      if (j.status === 'SUCCEEDED') {
        setNeedRefine(false);
        await refreshDeliverables(); // auto-refresh documents from the new design version
      } else {
        onChanged();
      }
    },
  });

  const onApprove = async () => {
    setApproving(true);
    setError(null);
    try {
      await approveDesign(project.projectId, project.version, getApiToken);
      setNotice(`Design v${project.designModelVersion} approved — Build is unlocked.`);
      onChanged();
    } catch (err) {
      setError(err.reason === 'locked'
        ? 'A generation job is running — approve once it finishes.'
        : err.reason === 'stale-version'
        ? 'This project changed — reopen it and retry.'
        : err.message);
    } finally {
      setApproving(false);
    }
  };

  const submit = async (mode, { feedback } = {}) => {
    setSubmitting(true);
    setError(null);
    setNotice(null);
    try {
      const res = await submitGeneration(
        project.projectId,
        { kind: 'design', mode, stage: 'design', ...(feedback ? { feedback } : {}) },
        getApiToken,
      );
      setEstimateModal(res);
      start(res.jobId, {
        jobId: res.jobId, status: res.status,
        estimatedCostUsd: res.estimate?.costUsd, estimatedDurationMs: res.estimate?.durationMs,
      });
    } catch (err) {
      setError(err.reason === 'locked' ? 'A generation job is already running on this project.' : err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Per-document "Upload revised" — route the marked-up file back into the design by type. ----
  const onUploadRevised = async (deliverable, file) => {
    if (!file) return;
    setError(null);
    setNotice(null);
    try {
      if (deliverable.type === 'DDD') {
        const diff = await diffDddWorkbook(file, designModel); // preview; applied on confirm
        setDddImport({ diff });
      } else if (deliverable.type === 'CUSTOMER_WORKSHEET') {
        const { answers, added } = await extractWorksheetAnswers(file, designModel?.openQuestions || []);
        if (answers.length === 0 && added.length === 0) {
          setNotice('No filled-in answers or new questions found in that worksheet.');
          return;
        }
        let expectedVersion = project.version;
        for (const a of answers) {
          const { project: updated } = await answerOpenQuestion(
            project.projectId, a.oqId, { expectedVersion, answer: a.answer, status: 'resolved' }, getApiToken,
          );
          expectedVersion = updated.version;
        }
        if (added.length) {
          const ops = added.map((a) => ({
            op: 'add',
            path: '/openQuestions',
            value: { question: a.question, category: a.category, ...(a.answer ? { status: 'resolved', answer: a.answer } : {}) },
          }));
          const res = await applyDesignEdits(project.projectId, expectedVersion, ops, getApiToken);
          expectedVersion = res.project.version;
        }
        const bits = [];
        if (answers.length) bits.push(`resolved ${answers.length}`);
        if (added.length) bits.push(`added ${added.length} new`);
        setNotice(`Open questions: ${bits.join(' · ')}.`);
        setNeedRefine(true);
        onChanged();
      } else {
        // NLU worksheet / anything else → register as a design-input source for the next refine.
        const { text, kind, contentType } = await extractText(file);
        await uploadSource(
          project.projectId, project.version,
          { filename: file.name, contentType, kind, text, role: 'design-input' }, getApiToken,
        );
        setNotice(`Uploaded "${file.name}" as a design input — Refine to fold it in.`);
        setNeedRefine(true);
        onChanged();
      }
    } catch (err) {
      setError(err.reason === 'locked'
        ? 'A generation job is running — uploads are paused until it finishes.'
        : err.reason === 'stale-version'
        ? 'This project changed — reopen it and retry.'
        : err.message);
    }
  };

  // §4.1 Regenerate-with-feedback: seed a Fast design refine with the NLU auditor's findings so the
  // NLU/Lex slices address them, then the auditor re-grades. Bounded — one refine round.
  const regenerateFromNluFindings = () => {
    const findings = designModel?.nluQualityGrade?.findings || [];
    if (!findings.length) return;
    const feedback = findings
      .map((f) => `[${f.severity || 'note'}] ${f.collection ? `${f.collection}: ` : ''}${f.issue || ''}${f.fix ? ` — ${f.fix}` : ''}`)
      .join('\n');
    submit('fast', { feedback });
  };

  const applyDddImport = async () => {
    if (!dddImport?.diff?.ops?.length) { setDddImport(null); setNeedRefine(true); return; }
    setImportBusy(true);
    setError(null);
    try {
      await applyDesignEdits(project.projectId, project.version, dddImport.diff.ops, getApiToken);
      const hadFreeform = dddImport.diff.adds.length > 0;
      setDddImport(null);
      setNotice('Applied the DDD edits to the design.');
      setNeedRefine(hadFreeform); // new rows still need the LLM refine to fold in
      onChanged();
    } catch (err) {
      setError(err.reason === 'locked'
        ? 'A generation job is running — applying edits is paused until it finishes.'
        : err.reason === 'stale-version'
        ? 'This project changed — reopen it and retry the import.'
        : err.message);
    } finally {
      setImportBusy(false);
    }
  };

  return (
    <Stack>
      {/* Zone 1 — compact design status + the Refine engine. */}
      <Card withBorder padding="lg" radius="md">
        <Group justify="space-between" align="flex-start" mb="sm">
          <div>
            <Group gap="xs" align="center">
              <Title order={4}>Design Model</Title>
              <Tooltip
                label="Total actual Bedrock spend across every Design pass on this project (source pre-extraction + generation)."
                withArrow
                multiline
                w={260}
              >
                <Badge variant="light" color="gray" leftSection={<IconCoin size={12} />}>
                  AI cost: {formatUsd(project.costByPhase?.design ?? project.costUsd ?? 0)} total
                </Badge>
              </Tooltip>
            </Group>
            {counts ? (
              <Text size="sm" c="dimmed">
                v{project.designModelVersion} · last refined {timeAgo(project.updatedAt)} ·
                {' '}{counts.intents} intents · {counts.prompts} prompts · {counts.flows} flows · {counts.openQuestions} open questions
              </Text>
            ) : (
              <Text size="sm" c="dimmed">No Design Model yet — run a first pass to generate it.</Text>
            )}
          </div>
          <Group gap="xs">
            <Tooltip
              label={hasModel
                ? <ActionHelp
                    reads="new/changed Source documents + answered open questions, merged into the existing design"
                    does="re-runs the light slices (NLU / prompts / integrations); your edits & resolved answers are preserved"
                    updates="the Design Model + design artifacts only — contact flows are untouched (regenerate them on the Diagrams tab)"
                  />
                : <ActionHelp
                    reads="your uploaded Source documents (weighted by each doc's role)"
                    does="generates the Design Model — NLU, prompts, integrations"
                    updates="creates the Design Model + deliverables (DDD / NLU / Customer worksheet). Contact flows are generated separately on the Diagrams tab."
                  />}
              withArrow multiline w={320}
            >
              <Button variant="default" loading={submitting && !running} disabled={running} onClick={() => submit('fast')}>
                {hasModel ? 'Refine (Fast)' : 'Fast draft'}
              </Button>
            </Tooltip>
            <Tooltip
              label={hasModel
                ? <ActionHelp
                    reads="new/changed Source documents + answered open questions, merged into the existing design"
                    does="runs the Fast pipeline PLUS an adversarial Design-Critic review (missing / invented / inconsistent vs the sources) whose findings drive one bounded refine (~2× cost)"
                    updates="the Design Model + design artifacts only — contact flows are untouched (regenerate them on the Diagrams tab)"
                  />
                : <ActionHelp
                    reads="your uploaded Source documents (weighted by each doc's role)"
                    does="generates the Design Model, then an adversarial Design-Critic review (missing / invented / inconsistent vs the sources) whose findings drive one bounded refine (~2× cost)"
                    updates="creates the Design Model + deliverables (DDD / NLU / Customer worksheet). Contact flows are generated separately on the Diagrams tab."
                  />}
              withArrow multiline w={320}
            >
              <Button color="orange" leftSection={<IconSparkles size={16} />} loading={submitting && !running} disabled={running} onClick={() => submit('deep')}>
                {hasModel ? 'Refine (Deep)' : 'Deep pass'}
              </Button>
            </Tooltip>
            {hasModel && (approvedCurrent ? (
              <Tooltip label={`Design v${project.designApprovedVersion} is approved — Build is unlocked. A later refine re-requires approval.`} withArrow multiline w={260}>
                <Badge size="lg" variant="light" color="green" leftSection={<IconCheck size={13} />}>
                  Approved v{project.designApprovedVersion}
                </Badge>
              </Tooltip>
            ) : (
              <Tooltip
                label={<ActionHelp
                  reads="the current Design Model version"
                  does="records your sign-off on this exact design version (§7 step 5)"
                  updates="unlocks the Build tab's soft gate — a later refine makes the approval stale and asks again"
                />}
                withArrow multiline w={300}
              >
                <Button variant="light" color="green" leftSection={<IconCheck size={16} />}
                  loading={approving} disabled={running || locked} onClick={onApprove}>
                  Approve design
                </Button>
              </Tooltip>
            ))}
          </Group>
        </Group>

        {hasModel && (
          <Text size="xs" c="dimmed">
            Refine <b>merges</b> into the existing design — your edits and answered questions are preserved;
            new sources, resolved answers, and uploaded revisions are folded in. It updates the design
            artifacts only; <b>contact flows are generated separately on the Diagrams tab</b> once the
            design is settled.
            {lexStrategy && <> · <b>Lex/NLU:</b> {lexStrategy.reason}</>}
            {' '}(Deep adds an adversarial critic review + one bounded refine of its findings, ~2× the Fast cost.)
          </Text>
        )}

        {needRefine && !running && (
          <Alert color="orange" mt="sm" icon={<IconSparkles size={16} />}>
            <Group justify="space-between">
              <Text size="sm">You uploaded revised documents — Refine to fully incorporate the changes.</Text>
              <Button size="xs" color="orange" onClick={() => submit('fast')}>Refine now</Button>
            </Group>
          </Alert>
        )}
        {notice && <Alert color="green" mt="sm" icon={<IconCheck size={16} />}>{notice}</Alert>}
        {error && <Alert color="red" mt="sm" icon={<IconAlertCircle size={16} />}>{error}</Alert>}

        <JobStatusAlert job={job} stalled={stalled} title="Design pass" onDismiss={dismiss}>
          {job?.status === 'SUCCEEDED' && job.result?.changeSummary && <ChangeSummary summary={job.result.changeSummary} />}
        </JobStatusAlert>
      </Card>

      {/* §4.1 — advisory NLU auditor grade (only when the design has a Lex bot). */}
      {hasModel && designModel?.nluQualityGrade && (
        <NluGradeCard
          grade={designModel.nluQualityGrade}
          onRegenerate={regenerateFromNluFindings}
          disabled={running || locked}
        />
      )}

      {/* Zone 2 — the document workspace. */}
      <DocumentsPanel
        project={project}
        designModel={designModel}
        hasModel={hasModel}
        getApiToken={getApiToken}
        docBusy={docBusy}
        onRefresh={refreshDeliverables}
        onUploadRevised={onUploadRevised}
      />

      {/* Zone 3 — open questions (folded in from the former tab). Answer in-app, or send them to the
          customer via the Customer Worksheet document above and upload the filled copy to resolve. */}
      {hasModel && (
        <OpenQuestionsSection
          project={project}
          designModel={designModel}
          getApiToken={getApiToken}
          onChanged={onChanged}
          locked={locked}
        />
      )}

      <Modal opened={!!estimateModal} onClose={() => setEstimateModal(null)} title="Generation submitted" centered>
        {estimateModal && (
          <Stack>
            <Text size="sm">A <b>{estimateModal.mode}</b> {estimateModal.kind} pass was queued under the generation lock.</Text>
            <Box>
              <Text size="sm">Up-front estimate (§6A):</Text>
              <Text size="sm">≈ <b>{formatUsd(estimateModal.estimate?.costUsd)}</b> · ≈ <b>{Math.round((estimateModal.estimate?.durationMs || 0) / 1000)}s</b></Text>
              <Text size="xs" c="dimmed">{estimateModal.estimate?.basis}</Text>
            </Box>
            <Button color="orange" onClick={() => setEstimateModal(null)}>OK</Button>
          </Stack>
        )}
      </Modal>

      <Modal opened={!!dddImport} onClose={() => setDddImport(null)} title="Apply revised DDD" centered>
        {dddImport && (
          <Stack>
            <Text size="sm">Diff of your revised DDD against the current design:</Text>
            <Group gap="xs">
              <Badge color="green" variant="light">{dddImport.diff.summary.autoApply} edit(s) to apply</Badge>
              {dddImport.diff.summary.removals > 0 && <Badge color="red" variant="light">{dddImport.diff.summary.removals} removal(s)</Badge>}
              {dddImport.diff.summary.adds > 0 && <Badge color="yellow" variant="light">{dddImport.diff.summary.adds} new row(s)</Badge>}
              {dddImport.diff.summary.unmapped > 0 && <Badge color="gray" variant="light">{dddImport.diff.summary.unmapped} unmapped</Badge>}
            </Group>
            {dddImport.diff.summary.removals > 0 && (
              <Text size="xs" c="dimmed">
                Removals come from rows you deleted from the sheet — they apply deterministically along
                with edits. New rows are folded in by the next Refine (you'll be prompted).
              </Text>
            )}
            <Group justify="flex-end" gap="xs">
              <Button variant="subtle" color="gray" onClick={() => setDddImport(null)}>Cancel</Button>
              <Button color="orange" loading={importBusy} onClick={applyDddImport}>
                {dddImport.diff.ops.length > 0 ? `Apply ${dddImport.diff.ops.length} change(s)` : 'Done'}
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}
